import { Button, Form, Modal, Select } from "antd";
import AccountSelector from "components/AccountSelector";
import MultiValueDisplay from "components/MultiValueDisplay";
import useBatchUpdate from "hooks/useBatchUpdate";
import usePackagesForAccount from "hooks/usePackagesForCustomer";
import { CustomerAccount } from "models/accountSummary";
import { CardBatch } from "models/cards";
import { StaffPackage } from "models/packages";
import { useState } from "react";
import { localizedDate } from "util/helpers";

interface EditBatchForm {
    account: CustomerAccount | undefined;
    packageId: string | undefined;
}

const { Option } = Select;

const EditBatchModal = (props: { batch: CardBatch; onSuccess: () => Promise<void> }) => {
    const [visible, setVisible] = useState<boolean>(false);
    const [working, setWorking] = useState<boolean>(false);
    const [form] = Form.useForm<EditBatchForm>();
    const { getPackagesForAccount } = usePackagesForAccount();
    const [packages, setPackages] = useState<StaffPackage[] | undefined>();
    const { updateBatch } = useBatchUpdate();
    const customerSelected = packages !== undefined;

    const onCustomerSelected = async (
        account: CustomerAccount | undefined
    ) => {
        form.setFieldsValue({ account });
        if (account) {
            setWorking(true);
            const pkgs = await getPackagesForAccount(account.id);
            setPackages(pkgs);
            setWorking(false);
        }
    };

    const onCustomerCleared = () => {
        form.setFieldsValue({ account: undefined });
        setPackages(undefined);
    };

    const closeAndResetModal = () => {
        setVisible(false);
        setPackages(undefined);
        form.resetFields();
    };

    const submitForm = async () => {
        const data = await form.validateFields();
        setWorking(true);

        try {
            await updateBatch({
                batchId: props.batch.id,
                newPackageId: data.packageId!,
                newAccount: data.account!,
            });
            await props.onSuccess();
            closeAndResetModal();
        } finally {
            setWorking(false);
        }
    };

    return (
        <>
            <Button type="link" onClick={() => setVisible(true)}>
                Edit
            </Button>
            <Modal
                title={`Edit Batch ${props.batch.number} - ${localizedDate(
                    props.batch.createdDate
                )}`}
                closable={false}
                keyboard={false}
                maskClosable={false}
                destroyOnClose={true}
                open={visible}
                confirmLoading={working}
                cancelButtonProps={{ disabled: working }}
                okText={working ? "Saving..." : "Update"}
                onOk={submitForm}
                onCancel={closeAndResetModal}
            >
                <Form form={form} disabled={working} layout="vertical">
                    <Form.Item
                        label="Change Customer To"
                        name="account"
                        rules={[
                            {
                                required: true,
                                message: "You must select a customer!",
                            },
                        ]}
                    >
                        <AccountSelector
                            onSelected={onCustomerSelected}
                            onClear={onCustomerCleared}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Assign To Package"
                        name="packageId"
                        rules={[
                            {
                                required: true,
                                message:
                                    "You must select a package to move the batch to!",
                            },
                        ]}
                    >
                        <Select
                            disabled={working || !customerSelected}
                            placeholder={
                                !customerSelected
                                    ? "Select a customer first"
                                    : "Select a package"
                            }
                            allowClear
                            showSearch
                            showArrow
                        >
                            {packages?.map((p) => (
                                <Option key={p.id} value={p.id}>
                                    <MultiValueDisplay
                                        title={`ID - ${p.friendlyId === 0
                                            ? "N/A"
                                            : p.friendlyId
                                            }`}
                                        subTitle={`Received on ${localizedDate(
                                            p.receivedDate
                                        )} - ${p.packageTypeDisplayName}`}
                                    />
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default EditBatchModal;
